import { isDownArrow, isEnd, isHome, isUpArrow } from '@starrez/starryui9';
import React, { createRef, useEffect, useRef } from 'react';
import { UL } from './ViewItem.styles';

interface IViewItemListProps {
    autoFocus?: boolean;
    subList?: boolean;
}

export const selectors = [
    `li > div > input`, // SearchInput
    `li > button:first-child`, // ViewItem & ViewItemWizard
    `li > [tabindex="0"]`, // ViewItemFolder
    `li[tabindex="0"]`, // No results messages & loaders
].join(', ');

export const ViewItemList: React.FC<IViewItemListProps> = ({ autoFocus, subList, children }) => {
    const focus = useRef<number>(0);
    const listRef = createRef<HTMLUListElement>();
    const prevChildren = useRef<React.ReactNode>();
    const focusableEl = useRef<Element[]>();

    useEffect(() => {
        const matchingEl = listRef.current?.querySelectorAll(selectors);

        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        focusableEl.current = [...(matchingEl as any)].filter((item: Element) => item.closest('ul') === listRef.current);
    }, [listRef, focusableEl]);

    useEffect(() => {
        // Refocus when focus is lost
        if (document.activeElement?.tagName === 'BODY') {
            focusableEl.current && (focusableEl.current[focus.current] as HTMLElement).focus();
        }

        if (React.Children.count(prevChildren.current) === 0) {
            // Focus on first element when open ViewItemFolder
            if (autoFocus) {
                focusableEl.current && (focusableEl.current[0] as HTMLElement).focus();
            }
        }

        prevChildren.current = children;
    }, [autoFocus, children, focusableEl]);

    const handleFocus = (e: React.KeyboardEvent<HTMLUListElement>) => {
        if (!focusableEl.current) return;

        let setFocus = false;
        let focusIndex = focusableEl.current.indexOf(document.activeElement as Element);

        if (isDownArrow(e)) {
            e.stopPropagation();
            ++focusIndex;
            setFocus = true;
        } else if (isUpArrow(e)) {
            e.stopPropagation();
            --focusIndex;
            setFocus = true;
        } else if (isHome(e) && !subList) {
            e.stopPropagation();
            focusIndex = 1;
            setFocus = true;
        } else if (isEnd(e) && !subList) {
            e.stopPropagation();
            focusIndex = focusableEl.current.length - 1;
            setFocus = true;
        }

        if (setFocus) {
            focusIndex = focusIndex < 0 ? 0 : Math.min(focusIndex, focusableEl.current.length - 1);
            (focusableEl.current[focusIndex] as HTMLElement).focus();
            focus.current = focusIndex;
        }
    };

    return (
        <UL role="listbox" ref={listRef} onKeyDown={(e) => handleFocus(e)}>
            {children}
        </UL>
    );
};
